import { Fragment, useCallback, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import { Form, Button } from "react-bootstrap";
import { useGetMemTable } from "../../hooks/useMemoryDB";
import Switch from "../switch";
import { useDataSaveItem } from "../../hooks/useData";
import { useItemContext } from "../../hooks/useItem";
import { useMainContext } from "../../hooks/useMainContext";
import useBaseVersion from "../../hooks/useBaseVersion";
import useLatestRef from "../../hooks/useLatestRef";
import Editor from "@monaco-editor/react";
import Container from "../container";
import ColleagueSaved from "../colleagueSaved";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArchive,
  faCircleCheck,
  faCircleXmark,
  faDatabase,
  faLock,
  faLockOpen,
  faTools,
  faUsers
} from "@fortawesome/free-solid-svg-icons";

import styles from "./styles.module.scss";
import "./global.scss";

const colors = [
  "#012a4a",
  "#013a63",
  "#01497c",
  "#014f86",
  "#936639",
  "#a68a64",
  "#b6ad90",
  "#c2c5aa",
  "#a4ac86",
  "#656d4a"
];

export default function Conversion(props) {
  const { values, errors, setFieldValue, dirty } = useFormikContext();
  const { isNew, setItemId } = useItemContext();
  // The stored params (a Params card each: its row, its own Save)
  const table = useGetMemTable("params");
  // The params of when the conversion was opened
  const [paramsIds] = useState(() => [...(table?.keys() ?? [])]);
  const [edit, forceSetEdit] = useState(false);

  const setEdit = useCallback(() => {
    forceSetEdit((state) => !state);
  }, []);

  const handleIdChange = useCallback(
    (e) => {
      const value = e.target.value;
      setFieldValue("id", value);
      setItemId(value);
    },
    [setFieldValue, setItemId]
  );

  const handleScopeChange = useCallback(
    (state) => {
      setFieldValue("scope", state ? "block" : "item");
    },
    [setFieldValue]
  );


  return (
    <>
      <Container
        title="Settings"
        foldable={false}
        color="#14213d"
        extra={
          <div className={styles.extraBox}>
            <Form.Check
              key="conversion-historicized"
              id="conversion-historicized"
              type="switch"
              label="Historicized"
              onChange={(e) => setFieldValue("historicized", e.target.checked)}
              checked={values.historicized || false}
              //disabled={!isNew}
            />
          </div>
        }
      >
        <Form.Label>Conversion Id</Form.Label>
        {isNew || edit ? (
          <Form.Control
            type="text"
            placeholder="Enter Conversion Id"
            isInvalid={errors.id}
            onChange={handleIdChange}
            value={values.id ?? ""}
            className={isNew ? styles.label : styles.labelDimmed}
            disabled={!isNew}
          />
        ) : (
          <div
            className={styles.conversionLabel}
            onDoubleClick={() => setEdit(true)}
          >
            {values.id}
          </div>
        )}
        <div className={styles.scope}>
          <Form.Label>Scope</Form.Label>
          <div
            onDoubleClick={() => setEdit()}
            className={`${styles.scopeLabel} ${
              values.scope === "block"
                ? styles.scopeLabelBlock
                : styles.scopeLabelItem
            }`}
          >
            {isNew || edit ? (
              <div className={styles.controls}>
                <Switch
                  bsSize="small"
                  value={values.scope === "block" ? true : false}
                  onText="Block"
                  onColor="warning"
                  offText="Item"
                  offColor="danger"
                  onChange={(elm, state) => handleScopeChange(state)}
                  //disabled={!isNew}
                />
                <Form.Check
                  key="conversion-include-params"
                  id="conversion-include-params"
                  type="switch"
                  label="Include Params"
                  onChange={(e) =>
                    setFieldValue("include.params", e.target.checked)
                  }
                  checked={values.include?.params ?? false}
                  //disabled={!isNew}
                />
                <Form.Check
                  key="conversion-include-context"
                  id="conversion-include-context"
                  type="switch"
                  label="Include Context"
                  onChange={(e) =>
                    setFieldValue("include.context", e.target.checked)
                  }
                  checked={values.include?.context ?? false}
                  //disabled={!isNew}
                />
                {values.scope === "block" && (
                  <Form.Check
                    key="conversion-include-ids"
                    id="conversion-include-ids"
                    type="switch"
                    label="Include Content Ids"
                    onChange={(e) =>
                      setFieldValue("include.contentIds", e.target.checked)
                    }
                    checked={values.include?.contentIds ?? false}
                    //disabled={!isNew}
                  />
                )}
                <Form.Check
                  key="conversion-timeless"
                  id="conversion-timeless"
                  type="switch"
                  label="Timeless"
                  onChange={(e) => setFieldValue("timeless", e.target.checked)}
                  checked={values.timeless || false}
                  //disabled={!isNew}
                />
              </div>
            ) : values.scope === "block" ? (
              <>
                <span>Block</span> <div className={styles.divider} />
                <span>
                  <span className={styles.includes}>
                    Include Content Ids:{" "}
                    {values.include?.contentIds ? (
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        className={styles.OK}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faCircleXmark}
                        className={styles.KO}
                      />
                    )}
                  </span>
                </span>
              </>
            ) : (
              <>
                <span>Item</span>
                <div className={styles.divider} />
              </>
            )}

            {!edit && !isNew && (
              <>
                <span className={styles.includes}>
                  Include Params:{" "}
                  {values.include?.params ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={styles.OK}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      className={styles.KO}
                    />
                  )}
                </span>
                <span className={styles.includes}>
                  Include Context:{" "}
                  {values.include?.context ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={styles.OK}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      className={styles.KO}
                    />
                  )}
                </span>
                <span className={styles.includes}>
                  Timeless:{" "}
                  {values.timeless ? (
                    <FontAwesomeIcon
                      icon={faCircleCheck}
                      className={styles.OK}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      className={styles.KO}
                    />
                  )}
                </span>
              </>
            )}
          </div>
        </div>
        {(values.include?.context || values.include?.params) &&
          (edit || isNew) && (
            <div className={styles.whiteContainer}>
              <Container
                title="Params & Context: White List"
                folded={false}
                coloredBars={false}
              >
                <Editor
                  width="100%"
                  height="24vh"
                  defaultLanguage="json"
                  value={JSON.stringify(
                    values.whiteList ?? { context: {}, params: {} }
                  )}
                  defaultValue="// Insert your code"
                  theme="vs-dark"
                  onChange={(txt) =>
                    setFieldValue("whiteList", JSON.parse(txt))
                  }
                  options={{ minimap: { enabled: false }, folding: true }}
                />
              </Container>
            </div>
          )}

        {values.historicized && (
          <Container
            title={
              <>
                <FontAwesomeIcon
                  icon={faDatabase}
                  className={styles.titleIcon}
                />{" "}
                User Conversion History Permanent Storage Params
              </>
            }
            foldable={edit}
            folded={!edit}
            color="#023047"
            className={styles.paramsBox}
            extra={
              <FontAwesomeIcon
                icon={edit ? faLockOpen : faLock}
                onDoubleClick={() => setEdit()}
                className={styles.lockIcon}
              />
            }
          >
            {paramsIds.map((id, index) => (
              <Params
                color={colors[index]}
                key={id}
                id={id}
                stored={table?.get(id)}
                label={values.label}
                conversionDirty={dirty}
              />
            ))}
          </Container>
        )}
      </Container>
    </>
  );
}

// The fields of the conversion in the params `id` (stored: the row in
// MemoryDB now), and their "Save Parameters": an explicit action of its
// own, saved at once (not by the Save of the conversion). The edits are
// based on the row the card was loaded from (the base): the save sends it,
// with baseVersion = its version (refused when a colleague saved the
// params meanwhile: the conflict dialog, the edits stay); a colleague's
// save replaces the card while it is unmodified, else the edits stay with
// a warning. conversionDirty: the conversion form changed, the confirm
// says that this button does not save the conversion.
export function Params(props) {
  const { id, stored, label, conversionDirty, color } = props;
  // conflictDialog: a refused save (409) opens the conflict dialog
  const saveItem = useDataSaveItem({ notify: true, conflictDialog: true });
  const { requestConfirm } = useMainContext();
  const [data, setData] = useState(() => cloneDeep(stored));
  const dataRef = useLatestRef(data);
  // The fields keep what is typed in their own state: a row put in the card
  // (a colleague's, reloaded) mounts them again
  const [epoch, setEpoch] = useState(0);
  const { stale, save, reload, baseVersion } = useBaseVersion({
    accessor: "params",
    id,
    live: stored,
    isDirty: (base) => !isEqual(dataRef.current, base),
    onAdopt: (row, reason) => {
      setData(row);
      if (reason !== "saved") setEpoch((epoch) => epoch + 1);
    }
  });

  const contentTypes = useMemo(
    () => Object.keys(data?.content?.types || {}),
    [data?.content]
  );

  const handleChange = useCallback(
    (value, contentType, field, userType) => {
      value = (value || "").trim();
      if (!value) return;

      setData((data) => {
        const next = cloneDeep(data);
        set(
          next,
          `users.clusters.types[${userType}].events[${label}].contentTypes[${contentType}].fields[${field}]`,
          value
        );
        return next;
      });
    },
    [label]
  );

  const onSaveParam = useCallback(() => {
    if (!data) return;

    const question = `Are you Really sure you want to Save params Id: ${id}?`;
    const message = conversionDirty
      ? `The conversion has unsaved changes: "Save Parameters" saves only the parameters ${id}, not the conversion (use Save for it). ${question}`
      : question;

    // The params of the click with the version they were read at: a
    // colleague's save while the confirm is open replaces an unmodified
    // card (its base moves), and the server then refuses these params
    // instead of taking them on their version
    const item = {
      accessor: "params",
      Id: id,
      prevId: id,
      isNew: false,
      data,
      skipdeps: true,
      baseVersion: baseVersion()
    };
    requestConfirm(message, () => save(saveItem, item));
  }, [baseVersion, conversionDirty, data, id, requestConfirm, save, saveItem]);

  // Not stored any more (and not edited)
  if (!data) return null;

  return (
    <Container
      color={color}
      title={
        <>
          <FontAwesomeIcon icon={faTools} className={styles.titleIcon} />{" "}
          {data.id}
        </>
      }
      folded={true}
      footer={
        <>
          <ColleagueSaved stale={stale} onReload={reload} />
          <Button size="sm" variant="outline-success" onClick={onSaveParam}>
            Save Parameters
          </Button>
        </>
      }
    >
      <Fragment key={epoch}>
        {Object.entries(data.users?.clusters?.types || {}).map(
          ([id, type], typeIndex) => (
            <UserType
              key={type.name}
              userTypeId={id}
              data={type}
              label={label}
              index={typeIndex}
              contentTypes={contentTypes}
              onChange={handleChange}
            />
          )
        )}
      </Fragment>
    </Container>
  );
}

function UserType(props) {
  const { data, userTypeId, label, contentTypes, onChange } = props;

  const handleChange = useCallback(
    (value, contentType, field) => {
      onChange(value, contentType, field, userTypeId);
    },
    [onChange, userTypeId]
  );

  return (
    <Container
      title={
        <>
          <FontAwesomeIcon icon={faUsers} className={styles.titleIcon} />{" "}
          {data.name}
        </>
      }
      coloredBars={false}
      folded={true}
      color="#001233"
    >
      {contentTypes.map((contentType, index) => (
        <ContentType
          key={`${contentType}-${index}`}
          type={contentType}
          data={data.events?.[label]?.contentTypes?.[contentType]?.fields || {}}
          onChange={handleChange}
        />
      ))}
    </Container>
  );
}

function ContentType(props) {
  const { type, data, onChange } = props;
  const [raw, setRaw] = useState(data.raw || "");
  const [simple, setSimple] = useState(data.simple || "");
  const [ordered, setOrdered] = useState(data.ordered || "");

  const handleChange = useCallback(
    (field, value) => {
      onChange(value, type, field);
    },
    [onChange, type]
  );

  return (
    <Container
      title={
        <>
          <FontAwesomeIcon icon={faArchive} className={styles.titleIcon} />{" "}
          {type}
        </>
      }
      folded={true}
      coloredBars={false}
    >
      <Form.Label className={styles.fieldLabel}>Raw</Form.Label>
      <Form.Control
        type="text"
        value={raw}
        isInvalid={raw === ""}
        onChange={(e) => {
          const value = e.target.value;
          setRaw(value);
          handleChange("raw", value);
        }}
      />
      <Form.Label className={styles.fieldLabel}>Simple</Form.Label>
      <Form.Control
        type="text"
        value={simple}
        isInvalid={simple === ""}
        onChange={(e) => {
          const value = e.target.value;
          setSimple(value);
          handleChange("simple", value);
        }}
      />
      <Form.Label className={styles.fieldLabel}>Ordered</Form.Label>
      <Form.Control
        type="text"
        value={ordered}
        isInvalid={ordered === ""}
        onChange={(e) => {
          const value = e.target.value;
          setOrdered(value);
          handleChange("ordered", value);
        }}
      />
    </Container>
  );
}
